import { useEffect, useState, type FormEvent } from 'react';
import { Alert, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { SubmitButton, UserSelect, type UserPick } from '../form';
import { get, post, statusOf } from '../../lib/api';

export function ResendEmailForm() {
    const { t } = useTranslation();
    const [users, setUsers] = useState<UserPick[]>([]);
    const [email, setEmail] = useState('');
    const [sendMe, setSendMe] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [serverError, setServerError] = useState('');
    const [info, setInfo] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        get<{ email: string; name: string; phoneNumber: string }[]>('home/getLocalUsers')
            .then((r) =>
                setUsers(
                    (r.data ?? []).map((u) => ({
                        value: u.email,
                        name: (u.name ?? '').trim(),
                        email: u.email,
                        phone: u.phoneNumber ?? '',
                    })),
                ),
            )
            .catch(() => setServerError(t('resend.error')));
    }, [t]);

    async function onSubmit(e: FormEvent) {
        e.preventDefault();
        if (!email) return;
        setSubmitting(true);
        setServerError('');
        setInfo('');
        setSuccess('');
        try {
            await post('ActiveDirectory/ResendConfirmationEmail', { Email: email, sendMeEmail: sendMe });
            setSuccess(t('resend.sent'));
            setEmail('');
            setSendMe(false);
        } catch (err) {
            if (statusOf(err) === 409) setInfo(t('resend.alreadyConfirmed'));
            else setServerError(t('resend.error'));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="pt-3" style={{ maxWidth: '34rem' }}>
            <p className="text-body-secondary">{t('resend.description')}</p>
            {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}
            {info && (
                <Alert variant="info" dismissible onClose={() => setInfo('')}>
                    {info}
                </Alert>
            )}
            {serverError && (
                <Alert variant="danger" dismissible onClose={() => setServerError('')}>
                    {serverError}
                </Alert>
            )}
            <Form noValidate onSubmit={onSubmit}>
                <Form.Group className="mb-3">
                    <Form.Label htmlFor="resend-user">{t('register.selectUser')}</Form.Label>
                    <UserSelect inputId="resend-user" users={users} value={email} onChange={setEmail} />
                </Form.Group>
                <Form.Check
                    type="checkbox"
                    id="resend-sendme"
                    className="mb-3"
                    label={t('resend.sendMeLink')}
                    checked={sendMe}
                    onChange={(e) => setSendMe(e.target.checked)}
                />
                <SubmitButton type="submit" loading={submitting} disabled={!email}>
                    {t('resend.submit')}
                </SubmitButton>
            </Form>
        </div>
    );
}
